import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Briefcase, Clock, CheckCircle2 } from "lucide-react";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";

const domains = [
  {
    category: "Backend Development",
    programs: [
      { name: "Java (Spring Boot)", duration: "3-6 Months", level: "Intermediate" },
      { name: "Python (Flask / Django)", duration: "3-6 Months", level: "Beginner to Advanced" },
      { name: "MySQL & Database Design", duration: "2-4 Months", level: "Beginner" },
    ],
  },
  {
    category: "Frontend Development",
    programs: [
      { name: "HTML, CSS & JavaScript", duration: "2-3 Months", level: "Beginner" },
      { name: "React.js", duration: "3-4 Months", level: "Intermediate" },
      { name: "MERN Full Stack", duration: "4-6 Months", level: "Intermediate to Advanced" },
    ],
  },
  {
    category: "Emerging Technologies",
    programs: [
      { name: "Blockchain Development", duration: "3-6 Months", level: "Advanced" },
      { name: "AI / ML / Deep Learning", duration: "4-6 Months", level: "Intermediate to Advanced" },
      { name: "Generative AI", duration: "3-4 Months", level: "Intermediate" },
      { name: "Agentic AI", duration: "3-4 Months", level: "Advanced" },
    ],
  },
];

const benefits = [
  { icon: Award, title: "Industry Certificates", desc: "Receive recognized certificates upon successful completion of each program." },
  { icon: Briefcase, title: "Real-World Projects", desc: "Work on live client projects and build a strong professional portfolio." },
  { icon: BookOpen, title: "Mentorship", desc: "Learn from experienced developers and industry professionals." },
  { icon: Clock, title: "Flexible Schedules", desc: "Programs designed to accommodate students with academic commitments." },
];

export default function InternshipsPage() {
  return (
    <div data-testid="internships-page">
      {/* Hero */}
      <Spotlight className="bg-neutral-950 grid-pattern pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4 animate-fade-in-up">Internship Program</p>
            <h1
              data-testid="internships-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              Internships
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed animate-fade-in-up animation-delay-400">
              Gain hands-on experience with real-world projects. Our internship programs are 
              designed to transform students into industry-ready professionals.
            </p>
          </div>
        </div>
      </Spotlight>

      {/* Benefits */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Why Join Us</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-16">
              Program Benefits
            </h2>
          </SectionTransition>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <SectionTransition key={b.title} delay={i * 0.1}>
                <div data-testid={`benefit-card-${i}`} className="text-center md:text-left">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5 mx-auto md:mx-0">
                    <b.icon size={24} className="text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </SectionTransition>
            ))}
          </div>
        </div>
      </section>

      {/* Domains */}
      <section className="bg-slate-50 py-24 md:py-32" data-testid="internship-domains">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Domains</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-16">
              Available Programs
            </h2>
          </SectionTransition>

          <div className="space-y-12">
            {domains.map((domain, di) => (
              <SectionTransition key={domain.category} delay={di * 0.15}>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    {domain.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {domain.programs.map((prog) => (
                      <div
                        key={prog.name}
                        data-testid={`program-${prog.name.toLowerCase().replace(/[\s/()]+/g, '-')}`}
                        className="p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-200 hover:shadow-md transition-all duration-300"
                      >
                        <h4 className="text-base font-semibold text-slate-900 mb-3">{prog.name}</h4>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1.5">
                            <Clock size={14} className="text-blue-500" />
                            {prog.duration}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <CheckCircle2 size={14} className="text-blue-500" />
                            {prog.level}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </SectionTransition>
            ))}
          </div>
        </div>
      </section>

      {/* Image + CTA */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <SectionTransition>
              <div className="rounded-2xl overflow-hidden border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1565598487929-b6aad2be0bc5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Students learning to code"
                  className="w-full h-[350px] md:h-[450px] object-cover"
                  loading="lazy"
                />
              </div>
            </SectionTransition>
            <SectionTransition delay={0.2}>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                Start Your Journey Today
              </h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">
                Whether you are a beginner or looking to level up your skills, our structured programs
                will equip you with the knowledge and practical experience to thrive in the tech industry.
                Apply now and take the first step toward your tech career.
              </p>
              <Link
                to="/contact"
                data-testid="internships-apply-btn"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 transition-all duration-200 active:scale-95"
              >
                Apply Now <ArrowRight size={16} />
              </Link>
            </SectionTransition>
          </div>
        </div>
      </section>
    </div>
  );
}
